import { Empty, Loading } from '@/components';
import { useUploadAttachments } from '@/features/attachment/_hooks/attachment';
import { useMe } from '@/features/authentication/_hooks/me';
import { useMessage } from '@/providers';
import { type FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  useComment,
  useCreateComment,
  useUpdateComment,
} from '../../../hooks/comment';
import { useCommentStackSiderView } from '../../../hooks/stack-sider-view';
import type {
  CommentFormSchema,
  UpdateCommentParamsSchema,
} from '../../../models/comment';
import { generatePinpointKey } from '../../../utils/generate-pinpoint-key';
import { usePinpoint } from '../../pinpoint';
import { CommentForm } from './CommentForm';

type FormValues = z.infer<typeof CommentFormSchema>;

export const CommentFormWrapper: FC = () => {
  const { t } = useTranslation();
  const stackSiderView = useCommentStackSiderView();
  const siderConfig = stackSiderView.lastItem?.config;

  invariant(siderConfig?.viewType === 'comment-form', t('unreachable-code'));

  const [searchParams, setSearchParams] = useSearchParams();
  const queryCommentId = searchParams.get('commentId');

  const { resetPinpoint } = usePinpoint();

  const { openMessage } = useMessage();
  const isEdit = siderConfig.isEditing;

  useEffect(() => {
    // コメントIDがあると新規作成完了後にcommentIdの箇所に遷移してしまう。
    // そのため、新規作成処理前にコメントIDを削除している。
    if (!isEdit && queryCommentId) {
      resetPinpoint();
      setSearchParams((prev) => {
        prev.delete('commentId');
        return prev;
      });
    }
  }, [isEdit, queryCommentId, resetPinpoint, setSearchParams]);

  const commentId = isEdit ? siderConfig.id : '';
  const comment = useComment(commentId);
  const me = useMe();

  const { uploadAttachments } = useUploadAttachments();
  const { mutateAsync: createComment } = useCreateComment();
  const { mutateAsync: updateComment } = useUpdateComment();

  const pinpoint = siderConfig.pinpoint ?? comment.data?.pinpoint;

  const handleSubmit = useCallback(
    async (values: FormValues, files: File[]) => {
      if (!me.data) {
        return;
      }

      let newCommentId = commentId;

      const attachmentIds =
        values.attachments?.map((attachment) => attachment.id) ?? [];

      if (files.length) {
        const uploadedAttachmentIds = await uploadAttachments(files);
        if (uploadedAttachmentIds?.length) {
          attachmentIds.push(...uploadedAttachmentIds);
        }
      }

      const commonParams: Omit<
        z.infer<typeof UpdateCommentParamsSchema>,
        'id'
      > = {
        attachmentIds,
        assigneeIds: values.assigneeIds,
        content: values.content,
        publishType: values.publishType,
        relatedUserIds: values.relatedUserIds,
        sharedOrganizationIds: values.sharedOrganizationIds,
        labelId: values.labelId,
        tagIds: values.tagIds,
      };

      if (isEdit) {
        await updateComment({
          ...commonParams,
          id: commentId,
        });
      } else {
        const res = await createComment({
          ...commonParams,
          key: 'project',
          objectId:
            stackSiderView.target.type === 'project'
              ? stackSiderView.target.projectId
              : '',
          resolved: false,
          pinpoint: siderConfig.pinpoint,
          pinpointKey: generatePinpointKey(
            siderConfig.pinpoint?.field || undefined,
          ),
        });

        newCommentId = res.id;
      }

      openMessage(
        'success',
        isEdit ? t('message-update-completed') : t('message-create-completed'),
      );

      if (isEdit) {
        stackSiderView.pop();
      } else {
        stackSiderView.set({
          payload: stackSiderView.list.toSpliced(-1, 1).concat({
            siderType: 'comment',
            config: {
              viewType: 'comment-details',
              id: newCommentId,
              target: siderConfig.target,
            },
          }),
          target: stackSiderView.target,
        });
      }
    },
    [
      commentId,
      createComment,
      isEdit,
      siderConfig,
      t,
      updateComment,
      me.data,
      stackSiderView,
      uploadAttachments,
      openMessage,
    ],
  );

  if (isEdit && (me.isPending || comment.isPending)) {
    return <Loading />;
  }

  if (comment.isError) {
    return <Empty />;
  }

  return (
    <CommentForm
      key={commentId}
      type={isEdit ? 'edit' : 'new'}
      pinpoint={pinpoint}
      initialValues={
        comment.data
          ? {
              assigneeIds: comment.data.assigneeIds,
              publishType: comment.data.publishType,
              relatedUserIds: comment.data.relatedUserIds,
              sharedOrganizationIds: comment.data.sharedOrganizationIds,
              content: comment.data.content,
              labelId: comment.data.labelId ?? null,
              attachments: comment.data.attachments,
              tagIds: comment.data.tagTargets.map((target) => target.tagId),
            }
          : {
              assigneeIds: me.data ? [me.data.id] : [],
            }
      }
      commentTarget={{
        key: 'project',
        objectId:
          stackSiderView.target.type === 'project'
            ? stackSiderView.target.projectId
            : '',
        objectAssigneeIds: siderConfig.target.objectAssigneeIds,
      }}
      onSubmit={handleSubmit}
      onFileChanged={(changed) => {
        stackSiderView.updateLastItem({
          config: {
            ...siderConfig,
            formValues: {
              uploadedFilesChanged: changed,
            },
          },
        });
      }}
    />
  );
};
